import { useEffect, useRef, useState } from "react";
import { App, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/client";
import {
  getEventSetting,
  getVersionEvents,
  type EventSetting,
  type VersionEventList,
} from "@/api/events";
import { PageTabs } from "@/components/Tabs";
import { useCurrentApp } from "@/hooks/useCurrentApp";
import { useAppStore } from "@/store";
import type { AppVersion } from "@/types";
import { eventListVersion } from "./component/editableVersion";
import {
  AddEventsDrawer,
  AddedEventsTable,
  EncryptionPanel,
  SubscriptionMethod,
  type EventPushConfig,
  type EventsTab,
} from "./component";

const EMPTY_VERSIONS: AppVersion[] = [];
const EMPTY_EVENTS: VersionEventList = { visible: false, events: [] };

const EMPTY_CONFIG: EventPushConfig = {
  requestUrl: "",
  urlVerified: false,
  callbackUrl: "",
  callbackItems: [],
  deliveryMode: "webhook",
  encryptKey: null,
  verificationToken: "",
};

function applySetting(
  current: EventPushConfig,
  setting: EventSetting,
): EventPushConfig {
  return {
    ...current,
    requestUrl: setting.requestUrl,
    urlVerified: setting.urlVerified,
    callbackUrl: setting.callbackUrl,
    callbackItems: setting.callbackItems,
    verificationToken: setting.verificationTokenMasked,
  };
}

function Events() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const app = useCurrentApp();
  const versions = useAppStore((s) => s.versions[app.id] ?? EMPTY_VERSIONS);
  const listVersion = eventListVersion(versions);
  const versionId = listVersion.id;
  const [tab, setTab] = useState<EventsTab>("config");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<EventPushConfig>(EMPTY_CONFIG);
  const [subscription, setSubscription] =
    useState<VersionEventList>(EMPTY_EVENTS);
  const messageRef = useRef(message);
  const tRef = useRef(t);
  messageRef.current = message;
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setConfig(EMPTY_CONFIG);
    void getEventSetting(app.id)
      .then((setting) => {
        if (!cancelled) setConfig((current) => applySetting(current, setting));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        messageRef.current.error(
          err instanceof ApiError && err.message
            ? err.message
            : tRef.current("events.settingLoadFailed"),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [app.id]);

  useEffect(() => {
    if (!versionId) {
      setSubscription(EMPTY_EVENTS);
      return;
    }
    let cancelled = false;
    void getVersionEvents(app.id, versionId)
      .then((list) => {
        if (!cancelled) setSubscription(list);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSubscription(EMPTY_EVENTS);
        messageRef.current.error(
          err instanceof ApiError && err.message
            ? err.message
            : tRef.current("events.listLoadFailed"),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [app.id, versionId]);

  const refreshEvents = () => {
    if (!versionId) return;
    void getVersionEvents(app.id, versionId)
      .then(setSubscription)
      .catch(() => {});
  };

  const refreshSetting = () => {
    void getEventSetting(app.id)
      .then((setting) => setConfig((current) => applySetting(current, setting)))
      .catch(() => {});
  };

  return (
    <div className="flex flex-col gap-5 m-5">
      <PageTabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: "config", label: t("events.tabConfig") },
          { key: "encryption", label: t("events.tabEncryption") },
        ]}
      />

      <Spin spinning={loading}>
        <div className="flex flex-col gap-5">
          {tab === "config" ? (
            <>
              <SubscriptionMethod
                config={config}
                onSave={(next) => {
                  setConfig((prev) => ({
                    ...prev,
                    ...next,
                    urlVerified: true,
                  }));
                  refreshSetting();
                  refreshEvents();
                }}
                onGoEncryption={() => setTab("encryption")}
              />
              {subscription.visible ? (
                <>
                  <AddedEventsTable
                    events={subscription.events}
                    versionId={versionId}
                    editable={listVersion.editable}
                    onAdd={() => setDrawerOpen(true)}
                    onChanged={refreshEvents}
                  />
                  {listVersion.editable ? (
                    <AddEventsDrawer
                      open={drawerOpen}
                      addedEventCodes={subscription.events.map(
                        (item) => item.eventCode,
                      )}
                      onClose={() => setDrawerOpen(false)}
                      onAdded={refreshEvents}
                    />
                  ) : null}
                </>
              ) : null}
            </>
          ) : (
            <EncryptionPanel
              config={config}
              onChange={setConfig}
              onGoConfig={() => setTab("config")}
            />
          )}
        </div>
      </Spin>
    </div>
  );
}

export default Events;
